import type { TFlowState } from '@besuites/auth/flow';
import type { TypeFlowChoice, TypeFlowScreen } from '@/types/auth/types.auth.flow';

const STUCK = new Set(['FLOW_NOT_FOUND', 'STEP_NOT_ALLOWED', 'UNAUTHENTICATED', 'SESSION_REVOKED', 'SIGN_UP_CLOSED']);

const textOf = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);

const codesOf = (value: unknown): string[] => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []);

export default function flowScreenOf(state: TFlowState, emitted: Readonly<Record<string, unknown>>, choice: TypeFlowChoice, codesSeen: boolean): TypeFlowScreen {
    if (state.phase === 'idle') return { id: 'idle' };
    if (state.phase === 'loading') return { id: 'working' };
    if (state.phase === 'error') return { id: 'idle' };
    if (state.phase === 'done') {
        const codes = codesOf(state.session.recoveryCodes ?? emitted.recoveryCodes);
        return codes.length > 0 && !codesSeen ? { id: 'recovery-codes', codes } : { id: 'done' };
    }
    if (state.error && STUCK.has(state.error.code)) return { id: 'stuck', code: state.error.code };

    const steps = state.allowed.map((item) => item.step);
    const has = (step: string): boolean => steps.includes(step);
    const codes = codesOf(emitted.recoveryCodes);
    if (codes.length > 0 && !codesSeen) return { id: 'recovery-codes', codes };

    if (state.allowed.some((item) => item.protocol === null)) {
        return { id: 'profile', step: state.allowed.find((item) => item.protocol === null)?.step ?? 'profile' };
    }
    if (has('totp.enroll.confirm')) return { id: 'totp-setup', step: 'totp.enroll.confirm', uri: textOf(emitted.otpauthUri), secret: textOf(emitted.secret) };
    if (has('passkey.register.verify')) return { id: 'passkey', step: 'passkey.register.verify', registering: true, ceremony: true };
    if (has('passkey.assert')) return { id: 'passkey', step: 'passkey.assert', registering: false, ceremony: true };
    if (has('password.set') && choice !== 'passkey') {
        return { id: 'new-password', step: 'password.set', passkeyStep: has('passkey.register.options') ? 'passkey.register.options' : null };
    }
    const destination = textOf(emitted.otpSentTo);
    if (has('email-otp.verify')) return { id: 'email-code', step: 'email-otp.verify', destination };
    if (has('password-recovery.verify-code')) return { id: 'email-code', step: 'password-recovery.verify-code', destination };
    if (choice === 'email-code' && has('email-otp.send')) return { id: 'working' };

    const firstFactor = has('password.verify');
    if (has('recovery-code.verify') && (choice === 'recovery-code' || (!has('totp.verify') && !firstFactor && !has('passkey.challenge')))) {
        return { id: 'recovery-code', step: 'recovery-code.verify' };
    }
    if (has('totp.verify') && (choice === 'totp' || (!firstFactor && choice !== 'passkey'))) return { id: 'totp', step: 'totp.verify' };
    if (firstFactor && choice !== 'passkey') return { id: 'password', step: 'password.verify' };
    if (has('passkey.challenge')) return { id: 'passkey', step: 'passkey.challenge', registering: false, ceremony: false };
    if (has('passkey.register.options')) return { id: 'passkey', step: 'passkey.register.options', registering: true, ceremony: false };
    if (firstFactor) return { id: 'password', step: 'password.verify' };
    if (has('totp.verify')) return { id: 'totp', step: 'totp.verify' };
    if (has('password.set')) return { id: 'new-password', step: 'password.set', passkeyStep: null };
    if (has('totp.enroll.start') || has('password-recovery.request') || has('email-otp.send')) return { id: 'working' };
    if (state.error) return { id: 'stuck', code: state.error.code };
    return { id: 'unknown', steps };
}
